import { useEffect, type FC, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUrlWithSearchParams } from "../../helpers/routeHelpers";
import { useProviderNetwork } from "../../helpers/context-hooks/useProviderNetwork";
import { useLoggedIn } from "../../helpers/loginHelpers";
import { useMctFlags } from "../../helpers/flag-hooks/useMctFlags";
import { useAppState } from "../../helpers/context-hooks/useAppState";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used for @link
import type { StateValidator } from "../StateValidator";

export const ProviderNetworkRoute: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const isLoggedIn = useLoggedIn();
  const { zipcode } = useAppState();
  const { tmpFeEnableProviderNetwork } = useMctFlags();
  const { enableProviderNetwork: anonEnableProviderNetwork } =
    useProviderNetwork();
  const enableProviderNetwork = isLoggedIn
    ? tmpFeEnableProviderNetwork
    : anonEnableProviderNetwork;

  const redirectUrl = useUrlWithSearchParams("/");
  const [searchParams] = useSearchParams();
  const zipSearchParam = searchParams.get("zip");
  const navigate = useNavigate();

  const hasAppStateZip = !!zipcode;
  const hasSearchParamZip = !!zipSearchParam;
  /**
   * Check `zip` has been set by `StateValidator`, otherwise the redirect will get overridden
   * @see {@link StateValidator}
   */
  const hasZipCode = hasAppStateZip && hasSearchParamZip;

  useEffect(() => {
    // Perform the redirect safely after rendering completes
    if (hasZipCode && !enableProviderNetwork) {
      // Provider Network not enabled, zip param set by StateValidator, go to landing page.
      navigate(redirectUrl, { replace: true });
    }
  }, [enableProviderNetwork, navigate, redirectUrl, hasZipCode]);

  // Prevent rendering the children or anything else while redirecting
  return enableProviderNetwork && hasAppStateZip ? <>{children}</> : null;
};
